"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.snapshotInvoicesForAccountingReconcile = snapshotInvoicesForAccountingReconcile;
exports.invoiceMonetaryOrDueFieldsChanged = invoiceMonetaryOrDueFieldsChanged;
exports.collectInvoiceIdsNeedingXeroReconcileAfterEdit = collectInvoiceIdsNeedingXeroReconcileAfterEdit;
exports.collectRevisionAccountingInvoiceDeltas = collectRevisionAccountingInvoiceDeltas;
exports.hasRevisionAccountingSyncWork = hasRevisionAccountingSyncWork;
const round_money_js_1 = require("../money/round-money.js");
/** Copy reconcile fields so later in-place entity mutation does not alias the before snapshot. */
function snapshotInvoicesForAccountingReconcile(invoices) {
    return invoices.map((invoice) => ({
        id: invoice.id,
        status: invoice.status,
        dueDate: invoice.dueDate,
        milestoneAmountCaptured: invoice.milestoneAmountCaptured,
        handshakeFeeAmountByBusinessCaptured: invoice.handshakeFeeAmountByBusinessCaptured,
        handshakeFeeAmountByClientCaptured: invoice.handshakeFeeAmountByClientCaptured,
    }));
}
function normalizeDueDay(value) {
    if (!value)
        return "";
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime()))
        return "";
    return d.toISOString().slice(0, 10);
}
function isPendingStatus(status) {
    return (status ?? "").toUpperCase() === "PENDING";
}
function isPaidStatus(status) {
    return (status ?? "").toUpperCase() === "PAID";
}
/** Pending invoices (full edit) and paid invoices (HAN-576 post-pay amount/fee changes). */
function isReconcileEligibleStatus(status) {
    return isPendingStatus(status) || isPaidStatus(status);
}
/** True when due date or captured amounts/fees changed for a reconcile-eligible invoice row. */
function invoiceMonetaryOrDueFieldsChanged(before, after) {
    if (normalizeDueDay(before.dueDate) !== normalizeDueDay(after.dueDate)) {
        return true;
    }
    if ((0, round_money_js_1.roundMoney)(before.milestoneAmountCaptured) !== (0, round_money_js_1.roundMoney)(after.milestoneAmountCaptured)) {
        return true;
    }
    if ((0, round_money_js_1.roundMoney)(before.handshakeFeeAmountByBusinessCaptured) !==
        (0, round_money_js_1.roundMoney)(after.handshakeFeeAmountByBusinessCaptured)) {
        return true;
    }
    if ((0, round_money_js_1.roundMoney)(after.handshakeFeeAmountByClientCaptured) !==
        (0, round_money_js_1.roundMoney)(before.handshakeFeeAmountByClientCaptured)) {
        return true;
    }
    return false;
}
/**
 * Existing pending/paid invoice ids whose due date / amounts changed.
 * Newly created ids are excluded (handled by post-create auto-sync).
 * Cancelled ids are excluded (handled by void).
 * Paid rows are included when captured amounts/fees change (HAN-576 post-pay deductions).
 */
function collectInvoiceIdsNeedingXeroReconcileAfterEdit(params) {
    const cancelled = new Set((params.cancelledIds ?? []).filter(Boolean));
    const beforeById = new Map(params.beforeInvoices.filter((inv) => inv.id).map((inv) => [inv.id, inv]));
    const ids = [];
    for (const after of params.afterInvoices) {
        if (!after?.id || cancelled.has(after.id))
            continue;
        if (!isReconcileEligibleStatus(after.status))
            continue;
        const before = beforeById.get(after.id);
        if (!before)
            continue;
        if (invoiceMonetaryOrDueFieldsChanged(before, after)) {
            ids.push(after.id);
        }
    }
    return ids;
}
/**
 * Invoice deltas after Handshake revision accept, used to enqueue accounting sync.
 * Created/removed lists stay pending-only. Reconcile includes paid rows when amounts/fees change.
 */
function collectRevisionAccountingInvoiceDeltas(params) {
    const beforeIds = new Set(params.beforeInvoices.map((inv) => inv.id).filter((id) => Boolean(id)));
    const afterIds = new Set(params.afterInvoices.map((inv) => inv.id).filter((id) => Boolean(id)));
    const createdInvoiceIds = params.afterInvoices
        .filter((inv) => Boolean(inv.id) && !beforeIds.has(inv.id) && isPendingStatus(inv.status))
        .map((inv) => inv.id);
    const reconcileInvoiceIds = collectInvoiceIdsNeedingXeroReconcileAfterEdit({
        beforeInvoices: params.beforeInvoices,
        afterInvoices: params.afterInvoices,
    });
    const removedInvoiceIds = params.projectInvoicesAtRemoval
        .filter((inv) => Boolean(inv.id) && !afterIds.has(inv.id) && isPendingStatus(inv.status))
        .map((inv) => inv.id);
    return { createdInvoiceIds, reconcileInvoiceIds, removedInvoiceIds };
}
function hasRevisionAccountingSyncWork(deltas, businessId) {
    if (!businessId) {
        return false;
    }
    return (deltas.createdInvoiceIds.length > 0 ||
        deltas.reconcileInvoiceIds.length > 0 ||
        deltas.removedInvoiceIds.length > 0);
}
